"use client";

import Link from "next/link";
import { ChevronDown } from "lucide-react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
} from "~/components/ui";
import { api } from "~/services/api";
import { cn } from "~/services/shadcn";
import { CategoryRowItem } from "./CategoryRowItem";
import { TopListHeader } from "./TopList";

const skeletonCategories = Array.from({ length: 11 }).map((_, i) => ({
  id: i,
  name: `category-${i}`,
  slug: `category-${i}`,
  totalItems: 100,
}));

export function TopCategoriesSection() {
  const { data: categories, isFetching } = api.categories.all.useQuery(
    {},
    {
      enabled: true,
    },
  );

  const sharedTopListProps = {
    title: "Top Categories",
    children: (
      <div className="ml-auto flex items-center space-x-2">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger className="ml-auto" asChild disabled>
            <Button variant="ghost" size="sm">
              <span>24h</span>

              <ChevronDown className="w-4 md:-mr-1 md:ml-0 md:w-5" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            <DropdownMenuItem>Past 24 hours</DropdownMenuItem>

            <DropdownMenuItem>This week</DropdownMenuItem>

            <DropdownMenuItem>This month</DropdownMenuItem>

            <DropdownMenuItem>This year</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="sm" disabled>
          View All
        </Button>
      </div>
    ),
  };

  return (
    <>
      {/* desktop */}
      <TopListHeader
        variant="heading"
        className="mt-[68px] hidden md:flex"
        {...sharedTopListProps}
      />

      {/* mobile */}
      <TopListHeader variant="nav" className="mt-[68px] md:hidden" {...sharedTopListProps} />

      <div className="mt-1 flex flex-col sm:flex-row">
        <div className={cn("relative flex flex-1 flex-col max-md:bg-black/50 max-md:px-4")}>
          {(isFetching ? skeletonCategories : categories)?.slice(0, 5)?.map((category, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              active={isFetching}
              className="mr-1 mt-1 h-16 sm:mr-2 sm:mt-2"
            >
              <Link
                href={`/categories/${category?.slug}`}
                key={`top-category-row-item-${category?.slug}`}
                className="border-b border-white/5 last:border-b-0"
              >
                <CategoryRowItem index={index} category={category} />
              </Link>
            </Skeleton>
          ))}
        </div>

        <div
          className={cn(
            "flex flex-1 flex-col border-l border-white/5 max-md:bg-black/50 max-md:px-4",
            /* display fewer items on mobile to save vertical space */
            "max-sm:mt-4 max-sm:hidden",
          )}
        >
          {(isFetching ? skeletonCategories : categories)?.slice(5, 10).map((category, index) => (
            <Skeleton
              key={`skeleton-${index + 5}`}
              active={isFetching}
              className="mr-1 mt-1 h-16 sm:mr-2 sm:mt-2"
            >
              <Link
                href={`/categories/${category?.slug}`}
                key={`top-category-row-item-${category?.slug}`}
                className="border-b border-white/5 last:border-b-0"
              >
                <CategoryRowItem index={index + 5} category={category} />
              </Link>
            </Skeleton>
          ))}
        </div>
      </div>
    </>
  );
}
